import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { toast } from 'sonner';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useSwitchBuilding } from '../hooks/useCurrentBuilding.js';
import { BuildingIcon, ChevronDownIcon, CheckIcon } from '../icons.jsx';

/**
 * Top-bar building picker. Dropdown lists user's accessible buildings (from /auth/me)
 * and posts to /auth/switch-building on selection.
 *
 * Hidden on Admin + Client routes (AppHeader handles that).
 */
export function BuildingSelector() {
  const { data: user } = useCurrentUser();
  const switchBuilding = useSwitchBuilding();

  if (!user) return null;
  const buildings = user.buildings || [];
  if (buildings.length === 0) return null;

  const current = user.currentBuilding;

  return (
    <Listbox
      value={current?.id ?? null}
      onChange={(id) => {
        if (id === current?.id) return;
        switchBuilding.mutate(id, {
          onSuccess: () => {
            const b = buildings.find((x) => x.id === id);
            toast.success(`Switched to ${b?.name ?? b?.code ?? 'building'}`);
          },
          onError: (err) => {
            toast.error(err.message || 'Could not switch building');
          },
        });
      }}
    >
      <div className="relative">
        <ListboxButton className="flex items-center gap-2 rounded-md border border-sky-400 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1">
          <BuildingIcon className="h-4 w-4 text-ink-500" />
          <span>{current ? current.code : 'Select building'}</span>
          <ChevronDownIcon className="h-4 w-4 text-ink-500" />
        </ListboxButton>

        <ListboxOptions
          anchor="bottom start"
          className="mt-1 w-64 overflow-hidden rounded-md border border-sky-300 bg-white shadow-md focus:outline-none [--anchor-gap:4px]"
        >
          {buildings.map((b) => (
            <ListboxOption
              key={b.id}
              value={b.id}
              className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-ink-900 data-[focus]:bg-sky-100 data-[selected]:bg-blue-600/10"
            >
              {({ selected }) => (
                <>
                  <span className="flex h-4 w-4 items-center justify-center">
                    {selected && <CheckIcon className="h-4 w-4 text-blue-600" />}
                  </span>
                  <span className="flex-1 truncate">
                    <span className="font-medium">{b.code}</span>
                    <span className="ml-2 text-ink-500">{b.name}</span>
                  </span>
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
